import { IAdminUser } from '@dadohr/shared/domainTypes/adminUser';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { USER_ROLE } from '@dadohr/shared/domainTypes/permissions';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@material-ui/core';
import { truncate } from 'lodash';
import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import shortid from 'shortid';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../../../../components/AdditionalActionsMenu';
import { AutocompleteMulti } from '../../../../../../../components/Autocomplete';
import { ButtonWithPromise } from '../../../../../../../components/ButtonWithPromise';
import { ColoredChip } from '../../../../../../../components/ColoredChip';
import {
  ID_TO_ROW_KEY,
  ItemSorters,
  RowsRenderer,
  RowsRendererScrollContainer,
  ROW_HEIGHTS
} from '../../../../../../../components/GroupableList';
import { PaperLoader } from '../../../../../../../components/Loader';
import { IColumn } from '../../../../../../../components/Table/VirtualizedSortable';
import { useDialogState } from '../../../../../../../hooks/useDialogState';
import { CanvasBar } from '../../../../../../../layout/Canvas';
import { FlexContainer } from '../../../../../../../layout/Flex';
import { Section } from '../../../../../../../layout/Section';
import { batchSetDocs } from '../../../../../../../services/firebase';
import { fromMoment } from '../../../../../../../services/time';
import { getUsersByOrgId } from '../../../../../../../services/user';
import {
  addToOrg,
  getAllAdminUsers,
  removeFromOrg,
  useAdminUsersInOrg
} from '../../../../../../services/adminUsers';

const HEIGHT = 200;

type ColumnName = 'id' | 'email' | 'role' | 'actions';
type D = Doc<IAdminUser>;
type OtherProps = { org: Doc<IOrganization> };
type Column = IColumn<D, ColumnName, OtherProps>;

const Actions = ({ d, o }: { d: D; o: OtherProps }) => {
  const orgId = o.org.id;
  const options: AdditionActionsMenuOption[] = useMemo(() => {
    return [
      {
        key: 'copyUserId',
        label: 'Copy User ID',
        onClick: () => {
          navigator.clipboard.writeText(d.id);
        }
      },
      {
        key: 'removeFromOrg',
        label: 'Remove from Organization',
        dialog: ({ onClose }) => {
          return (
            <>
              <DialogTitle>Remove from organization?</DialogTitle>
              <DialogContent>
                Please confirm to remove <strong>{d.data.email}</strong> from
                this organization.
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <ButtonWithPromise
                  variant="contained"
                  color="secondary"
                  onClick={() => removeFromOrg(d.id, orgId)}
                  pending="Sending..."
                >
                  Remove
                </ButtonWithPromise>
              </DialogActions>
            </>
          );
        }
      }
    ];
  }, [d, orgId]);
  return <AdditionalActionsMenu options={options} />;
};

const COLUMNS: Column[] = [
  {
    key: 'id',
    head: () => 'ID',
    cell: (d) => <div>{truncate(d.id, { length: 12 })}</div>,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 0
  },
  {
    key: 'email',
    head: () => 'Email',
    cell: (d) => d.data.email,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'role',
    head: () => 'Role',
    cell: (d, o) => {
      const v2 = o.org.data.permissions[d.id]?.v2;
      const role = v2?.role || USER_ROLE.EXPERIENCE_MANAGER;
      const additionalExpTplScopes =
        (
          Object.values(
            o.org.data.permissions[d.id]?.v2.additionalExpTplScopes || {}
          ) || []
        ).filter((x) => x.length).length || 0;
      const roleAsString =
        role === USER_ROLE.ACCOUNT_ADMIN
          ? 'Account Administrator'
          : role === USER_ROLE.EXPERIENCE_MANAGER
          ? 'Experience Manager'
          : '';
      if (roleAsString) {
        return (
          <FlexContainer>
            <div>{roleAsString}</div>
            {role === USER_ROLE.EXPERIENCE_MANAGER && (
              <ColoredChip mode="light" color="gray" size="small">
                {additionalExpTplScopes}
              </ColoredChip>
            )}
          </FlexContainer>
        );
      }
      return null;
    },
    sortable: false,
    width: 150,
    align: 'left',
    flexGrow: 1
  },
  {
    key: 'actions',
    head: () => '',
    alternateHead: () => 'Actions',
    cell: (d, o) => <Actions d={d} o={o} />,
    sortable: false,
    align: 'right',
    width: 50,
    flexGrow: 0
  }
];

const SORTERS: ItemSorters<D, OtherProps> = {
  email: {
    key: 'email',
    items: { sort: (d) => d.data.email, dir: 'asc' }
  }
};

const DEFAULT_SORTER = SORTERS.email;

const AddAdminUserDialog = ({
  open,
  onClose,
  orgId,
  currentAdmins
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  currentAdmins: Doc<IAdminUser>[];
}) => {
  const [nextAdmins, setNextAdmins] = useState<Doc<IAdminUser>[]>([]);

  const toIgnore = useMemo(() => {
    return new Set([
      ...nextAdmins.map((d) => d.id),
      ...currentAdmins.map((d) => d.id)
    ]);
  }, [currentAdmins, nextAdmins]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add admin users</DialogTitle>
      <DialogContent>
        <AutocompleteMulti
          value={nextAdmins}
          onChange={setNextAdmins}
          fullWidth
          options={getAllAdminUsers}
          getOptionLabel={(a) => a.data.email}
          filterOptions={(as, s) =>
            as.filter(
              (a) => !toIgnore.has(a.id) && a.data.email.includes(s.inputValue)
            )
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <ButtonWithPromise
          variant="contained"
          color="secondary"
          disabled={!nextAdmins.length}
          onClick={() =>
            Promise.all(nextAdmins.map((a) => addToOrg(a.id, orgId)))
          }
          pending="Adding..."
        >
          Add
        </ButtonWithPromise>
      </DialogActions>
    </Dialog>
  );
};

const Buttons = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',

  '> :not(:first-of-type)': {
    marginLeft: p.theme.spacing()
  }
}));

// @guide Add a field to the IUser object - 2.6.1 Add to super admin user helper
const adminUserToUser = (admin: Doc<IAdminUser>, orgId: string): Doc<IUser> => {
  return {
    id: shortid(),
    collection: FS.users,
    data: {
      firstName: admin.data.firstName,
      middleName: admin.data.middleName,
      preferredName: admin.data.preferredName,
      lastName: admin.data.lastName,
      gender: 'u',

      role: '',
      department: '',
      team: '',
      location: '',

      email: admin.data.email,
      personalEmail: '',
      managerId: '',
      orgId,
      alternateId: '',

      hireDate: fromMoment(moment().subtract()),
      terminationDate: null,
      dateOfBirth: null,
      address: null,
      phoneNumber: '',
      customFields: {},
      active: true
    }
  };
};

export const AdminUsers: React.FC<{
  org: Doc<IOrganization>;
}> = ({ org }) => {
  const [adminUsers, loading] = useAdminUsersInOrg(org.id);
  const addDialog = useDialogState(false);

  return (
    <Section>
      <CanvasBar>
        <div>Admin Users</div>
        <Buttons>
          <ButtonWithPromise
            size="small"
            variant="contained"
            color="primary"
            onClick={async () => {
              console.log(adminUsers);
              if (!adminUsers) {
                return;
              }
              const allUsers = await getUsersByOrgId(org.id);
              const allUserEmails = new Set(allUsers.map((u) => u.data.email));
              const missingAdmins = adminUsers.filter(
                (a) => !allUserEmails.has(a.data.email)
              );
              const newUsers = missingAdmins.map((a) =>
                adminUserToUser(a, org.id)
              );
              console.log('new', newUsers);
              await batchSetDocs(newUsers).catch((err) => console.log(err));
            }}
            disabled={!adminUsers}
            pending="Adding..."
          >
            Add admins as regular users
          </ButtonWithPromise>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={addDialog.openDialog}
            disabled={!adminUsers}
          >
            Add...
          </Button>
        </Buttons>
      </CanvasBar>
      {(loading || !adminUsers) && <PaperLoader height={HEIGHT} />}
      {!!adminUsers && (
        <RowsRendererScrollContainer height={HEIGHT}>
          <RowsRenderer
            variant="contained"
            columns={COLUMNS}
            rows={adminUsers}
            rowToKey={ID_TO_ROW_KEY}
            sorter={DEFAULT_SORTER}
            sortDirection={DEFAULT_SORTER.items.dir}
            renderHead={true}
            headProps={{ sticky: true }}
            chunkSize={30}
            rootMargin="300px"
            rowHeight={ROW_HEIGHTS.dense}
            otherProps={{ org }}
          />
        </RowsRendererScrollContainer>
      )}
      <AddAdminUserDialog
        open={addDialog.dialogOpen}
        onClose={addDialog.closeDialog}
        orgId={org.id}
        currentAdmins={adminUsers || EMPTY_ARR}
      />
    </Section>
  );
};
